import { Dispatch, SetStateAction } from "react";
import {
  assets,
  btnDesign,
  buttonStyle,
  colors,
  colorsRGB,
  fontWeights,
  gradients,
  marginBottom,
  mediaQueryWidths,
} from "./constants";

import {
  SanityImage,
  SanityTextCta,
  Maybe,
  SanityProduct,
  SanityBrand,
  SanityLeftRightBlock,
  SanityMediaCarousel,
  SanityMegaNav,
  SanityVideo,
  SanityImageWithMeta,
  SanityRange,
  SanityBrandOrRangeList,
  SanityWhereToBuyList,
  SanityCareersList,
  SanityReviewsBlock,
  SanityArticlesDisplay,
  SanityNews,
  SanityTextSlider,
  SanityJotFormBlock,
  SanityBlockContentWithMargin,
  SanityVideoCarousel,
} from "@graphql-types";

export type Asset = keyof typeof assets;
export type Color = keyof typeof colors;
export type ColorRGB = keyof typeof colorsRGB;
export type ButtonTheme = keyof typeof buttonStyle;
export type ButtonType = typeof buttonStyle;
export type FontWeights = keyof typeof fontWeights;
export type AssetType = typeof assets;
export type Gradient = keyof typeof gradients;
export type MediaQueryWidths = keyof typeof mediaQueryWidths;
export type MarginBottomKeys = keyof typeof marginBottom;

export type SetRegion = Dispatch<
  SetStateAction<{
    iban: string;
    langCode: string;
  }>
>;

export type AnimationDirection = {
  to: {
    transform?: string;
    opacity?: number;
  };
  from: {
    transform?: string;
    opacity?: number;
  };
};

export function isSanityImage(data: any): data is SanityImage {
  return data._type === "image";
}

export function isSanityTextCta(data: any): data is SanityTextCta {
  return data._type === "textCta";
}

export function isSanityMegaNav(data: any): data is SanityMegaNav {
  return data._type === "megaNav";
}

export interface ScreenWidth {
  isTabletWidth: boolean;
  isMobileWidth: boolean;
  isLaptopWidth: boolean;
}

export interface ScreenSize {
  smallDown: boolean;
  smallUp: boolean;
  mediumDown: boolean;
  medium: boolean;
  mediumUp: boolean;
  largeDown: boolean;
  large: boolean;
  largeUp: boolean;
  wideDown: boolean;
  wideUp: boolean;
}

// sanity > helper.js
export type ContainerSize = "wide" | "default" | "defaultFixed" | Maybe<string>;
export type ButtonDesign = keyof typeof btnDesign;
export type ButtonSize = "default" | "l" | Maybe<string>;
export type IconKind = "iconRightArrow" | Maybe<string>;
export type HeaderDesign = "transparent" | "solid" | "transparentDarkText";
export type MegaNavDesign = "designBrand" | "designPlain" | Maybe<string>;
export type HeadingDesign = "h1" | "h2" | "h3" | Maybe<string>;
export type ProductOrBrandOrRange = SanityProduct | SanityBrand | SanityRange;
export type ArrayOfProductOrBrandOrRange = SanityProduct[] | SanityBrand[] | SanityRange[];
export type StoreQueryVarible = "[DYNAMIC_ITEM]";

export interface Region {
  iban: string;
}

export type InternalLinkCustom = {
  _type: string;
  slug: { current: string };
  region: { iban: string };
  internal: { type: string };
};

export function isSanityProduct(data: any): data is SanityProduct {
  return data._type === "product";
}

export function isSanityBlockContentWithMargin(data: any): data is SanityBlockContentWithMargin {
  return data._type === "blockContentWithMargin";
}

export function isSanityBrand(data: any): data is SanityBrand {
  return data._type === "brand";
}

export function isSanityLeftRightBlock(data: any): data is SanityLeftRightBlock {
  return data._type === "leftRightBlock";
}

export function isSanityBrandOrRangeList(data: any): data is SanityBrandOrRangeList {
  return data._type === "brandOrRangeList";
}

export function isSanityMediaCarousel(data: any): data is SanityMediaCarousel {
  return data._type === "mediaCarousel";
}

export function isSanityVideo(data: any): data is SanityVideo {
  return data._type === "video";
}
export function isSanityImageWithMeta(data: any): data is SanityImageWithMeta {
  return data._type === "imageWithMeta";
}

export function isSanityRange(data: any): data is SanityRange {
  return data._type === "range";
}

export function isSanityWhereToBuyList(data: any): data is SanityWhereToBuyList {
  return data._type === "whereToBuyList";
}
export function isSanityCareersList(data: any): data is SanityCareersList {
  return data._type === "careersList";
}

export function isSanityReviewsBlock(data: any): data is SanityReviewsBlock {
  return data._type === "reviewsBlock";
}

export function isSanityArticlesDisplay(data: any): data is SanityArticlesDisplay {
  return data._type === "articlesDisplay";
}

export function isSanityNews(data: any): data is SanityNews {
  return data._type === "news";
}

export function isSanityJotFormBlock(data: any): data is SanityJotFormBlock {
  return data._type === "jotFormBlock";
}

export function isSanityTextSlider(data: any): data is SanityTextSlider {
  return data._type === "textSlider";
}

export function isSanityVideoCarousel(data: any): data is SanityVideoCarousel {
  return data._type === "videoCarousel";
}
